import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import { useAuth } from "@/providers";
import { ResourceFormActions } from "@/shared/components";
import { INGESTED_ELSEWHERE } from "@/shared/components/ingestionRules";
import { ActionInvocationEditor } from "@/shared/components/invocationSpec/ActionInvocationEditor";
import { exampleFileNameFor } from "@/shared/components/invocationSpec/parameterForms/exampleFileName";
import {
  submitError,
  type SubmitError,
} from "@/shared/components/ResourceForm";
import { isIngestionAction } from "@/shared/domain/actions";
import {
  type CreateIngestionRuleRequest,
  type IngestionRulePrefill,
  type IngestionRuleRecord,
} from "@/shared/domain/ingestionRules";
import {
  Field,
  FormSection,
  Radio,
  RadioGroup,
  TagInput,
  Textarea,
  TextInput,
} from "@/shared/foundation/primitives";

import { DefaultRulePicker } from "./DefaultRulePicker";
import styles from "./IngestionRuleForm.module.css";
import {
  buildIngestionRuleRequest,
  defaultRuleToFormValues,
  emptyIngestionRuleFormValues,
  ingestionRuleFormSchema,
  ingestionRulePrefillToFormValues,
  ingestionRuleToFormValues,
  type IngestionRuleFormOutput,
  type IngestionRuleFormValues,
} from "./ingestionRuleFormSchema";
import { NotAnIngestionActionWarning } from "./IngestionRuleNudge";
import { PausedBanner } from "./PausedBanner";
import { RuleOverlapWarning } from "./RuleOverlapWarning";
import { UploadedFileInput } from "./UploadedFileInput";

export type IngestionRuleFormProps =
  /** `prefill` seeds a new rule sent here from a file or a trigger. */
  (
    | { mode: "create"; prefill?: IngestionRulePrefill | null }
    | { mode: "edit"; initial: IngestionRuleRecord }
  ) & {
    /** Resolves when the page's mutation settles; a rejection is shown above the submit. */
    onSubmit: (request: CreateIngestionRuleRequest) => Promise<void>;
    onCancel: () => void;
  };

/** Suggestions offered while typing a pattern, for the formats Roboto ingests. */
const PATTERN_SUGGESTIONS: readonly string[] = [
  "**/*.mcap",
  "**/*.bag",
  "**/*.db3",
  "**/*.ulg",
  "**/*.csv",
  "**/*.parquet",
  "**/*.bin",
];

/** What ingests the matching files, as the form's radio group names it. */
type IngestedByChoice = "action" | "outside";

/** Create or edit one ingestion rule. */
export function IngestionRuleForm(props: IngestionRuleFormProps) {
  const { onSubmit, onCancel, mode } = props;
  const form = useForm<
    IngestionRuleFormValues,
    unknown,
    IngestionRuleFormOutput
  >({
    resolver: zodResolver(ingestionRuleFormSchema),
    defaultValues: initialValues(props),
  });
  const { control, setValue } = form;
  const { currentOrganization } = useAuth();
  // A new rule is created in the current org.
  const ruleOrgId =
    props.mode === "edit" ? props.initial.org_id : currentOrganization?.org_id;
  const includePatterns = useWatch({ control, name: "includePatterns" });
  const autoIngest = useWatch({ control, name: "autoIngest" });
  // Choosing "Something besides this rule" keeps the saved action, so the rule is paused
  // rather than stripped of it.
  const savedAction =
    props.mode === "edit"
      ? props.initial.invocation?.action.name ?? null
      : null;
  const resume = () => setValue("autoIngest", true);

  const nameId = React.useId();
  const includeId = React.useId();
  const excludeId = React.useId();
  const ingestedById = React.useId();

  const [serverError, setServerError] = React.useState<SubmitError | null>(
    null,
  );

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(buildIngestionRuleRequest(values));
  });

  return (
    <FormProvider {...form}>
      {/* Outside the form, so Enter in its search box can't submit the rule.
          Not for a rule prefilled from a file or a trigger, which picking a
          default would overwrite. */}
      {props.mode === "create" && !props.prefill && (
        <DefaultRulePicker
          className={styles.picker}
          onPick={(rule) =>
            form.reset(defaultRuleToFormValues(rule, form.getValues()))
          }
        />
      )}
      <form
        className={styles.form}
        data-testid="ingestion-rule-form"
        noValidate
        onSubmit={(event) => {
          setServerError(null);
          submit(event).catch((error: unknown) => {
            setServerError(submitError(error, mode, "ingestion rule"));
          });
        }}
      >
        {!autoIngest && savedAction !== null && (
          <PausedBanner actionName={savedAction} onResume={resume} />
        )}

        <FormSection title="Details">
          <div className={styles.fields}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <Field.Root name={field.name} invalid={fieldState.invalid}>
                  <Field.Label id={nameId}>Name</Field.Label>
                  <Field.Control
                    aria-labelledby={nameId}
                    render={
                      <TextInput
                        autoComplete="off"
                        spellCheck={false}
                        size="lg"
                      />
                    }
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    placeholder="MCAP recordings"
                  />
                  <Field.Error match>{fieldState.error?.message}</Field.Error>
                </Field.Root>
              )}
            />
            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <Field.Root name={field.name}>
                  <Field.Label>
                    Description{" "}
                    <span className={styles.optional}>(optional)</span>
                  </Field.Label>
                  <Field.Control
                    render={<Textarea rows={2} />}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                </Field.Root>
              )}
            />
          </div>
        </FormSection>

        <FormSection
          title="Which files"
          hint="Sessions aren’t complete until matching files are ingested. Checked against files uploaded to any dataset in this organization."
        >
          <div className={styles.fields}>
            <Controller
              control={control}
              name="includePatterns"
              render={({ field, fieldState }) => (
                <Field.Root invalid={fieldState.invalid}>
                  <Field.Label id={includeId}>Matches</Field.Label>
                  <TagInput
                    tags={field.value}
                    onChange={field.onChange}
                    placeholder="**/*.mcap"
                    suggestions={PATTERN_SUGGESTIONS}
                    size="lg"
                    aria-labelledby={includeId}
                    ref={field.ref}
                  />
                  <Field.Description>
                    Glob against the file&rsquo;s path in its dataset. Press
                    Enter after each.
                  </Field.Description>
                  <Field.Error match>{fieldState.error?.message}</Field.Error>
                </Field.Root>
              )}
            />
            <Controller
              control={control}
              name="excludePatterns"
              render={({ field }) => (
                <Field.Root>
                  <Field.Label id={excludeId}>
                    Except <span className={styles.optional}>(optional)</span>
                  </Field.Label>
                  <TagInput
                    tags={field.value}
                    onChange={field.onChange}
                    placeholder="**/scratch/**"
                    size="lg"
                    aria-labelledby={excludeId}
                  />
                  <Field.Description>
                    Files matching any of these are left out, even if they match
                    above.
                  </Field.Description>
                </Field.Root>
              )}
            />
          </div>
          {autoIngest && (
            <RuleOverlapWarning
              includePatterns={includePatterns}
              ruleId={props.mode === "edit" ? props.initial.rule_id : undefined}
            />
          )}
        </FormSection>

        <FormSection
          title="What ingests matching files?"
          titleId={ingestedById}
        >
          <div className={styles.fields}>
            <Controller
              control={control}
              name="autoIngest"
              render={({ field }) => (
                <RadioGroup<IngestedByChoice>
                  aria-labelledby={ingestedById}
                  appearance="card"
                  size="md"
                  name={field.name}
                  value={field.value ? "action" : "outside"}
                  onValueChange={(next) => field.onChange(next === "action")}
                >
                  <Radio<IngestedByChoice>
                    value="action"
                    label="Auto-ingest with an action"
                    description="This rule runs an action on each matching file when it’s uploaded, as the organization’s service account."
                  />
                  <Radio<IngestedByChoice>
                    value="outside"
                    label="Something besides this rule"
                    description={`${INGESTED_ELSEWHERE} Sessions still wait until these files are ingested.`}
                  />
                </RadioGroup>
              )}
            />
            {autoIngest && (
              <ActionInvocationEditor
                name="invocation"
                requiredParametersName="requiredParameters"
                defaultActionOwner={ruleOrgId}
                actionHint="Ingestion actions are listed first. Its parameters appear once you pick one."
                inputDataHeader={<UploadedFileInput />}
                // The uploaded file's dataset is in the event, so output can go
                // back to it and inputs can be drawn from it.
                hasSourceDataset
                triggeringSubject="file"
                startsOnUpload
                ingestionActionsFirst
                renderActionNotice={(action) =>
                  action !== null && !isIngestionAction(action) ? (
                    <NotAnIngestionActionWarning />
                  ) : null
                }
                exampleFileName={
                  includePatterns.length === 0
                    ? undefined
                    : exampleFileNameFor(includePatterns[0], "example")
                }
                data-testid="ingestion-rule-invocation"
              />
            )}
          </div>
        </FormSection>

        <ResourceFormActions
          submitLabel={mode === "create" ? "Create rule" : "Save changes"}
          idleHint={
            mode === "create"
              ? "Fill in the sections above, then create the rule."
              : "Changes apply to files uploaded after you save."
          }
          onCancel={onCancel}
          submitKind={mode}
          serverError={serverError}
        />
      </form>
    </FormProvider>
  );
}

function initialValues(props: IngestionRuleFormProps): IngestionRuleFormValues {
  if (props.mode === "edit") {
    return ingestionRuleToFormValues(props.initial);
  }
  return props.prefill === undefined || props.prefill === null
    ? emptyIngestionRuleFormValues()
    : ingestionRulePrefillToFormValues(props.prefill);
}
